import { useEffect, useRef } from 'react'
import { Rock } from '../../types'
import RockCheckInForm from './RockCheckInForm'
import { useFocusTrap } from '../shared/useFocusTrap'

interface Props {
  rock: Rock
  teamId: number
  meetingId?: number
  onClose: () => void
  notePlaceholder?: string
  onPrevRock?: () => void
  onNextRock?: () => void
  /** Called after a successful save; parent advances to the next rock or closes. */
  onSaved?: () => void
}

export default function RockCheckInModal({ rock, teamId, meetingId, onClose, notePlaceholder, onPrevRock, onNextRock, onSaved }: Props) {
  const showNav = !!onPrevRock || !!onNextRock
  const containerRef = useRef<HTMLDivElement>(null)
  useFocusTrap(containerRef, true)

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div ref={containerRef} className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="text-lg font-semibold text-text-primary font-heading">Check In</h3>
          {showNav && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={onPrevRock}
                disabled={!onPrevRock}
                aria-label="Previous rock"
                className="p-1.5 rounded-lg border border-stone/30 text-text-secondary hover:bg-[#F8F9FA] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={onNextRock}
                disabled={!onNextRock}
                aria-label="Next rock"
                className="p-1.5 rounded-lg border border-stone/30 text-text-secondary hover:bg-[#F8F9FA] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <p className="text-sm text-text-secondary mb-4">{rock.title}</p>

        <RockCheckInForm
          key={rock.id}
          rock={rock}
          teamId={teamId}
          meetingId={meetingId}
          onClose={onClose}
          notePlaceholder={notePlaceholder}
          onSaved={onSaved}
        />
      </div>
    </div>
  )
}
